/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useState } from 'react';

// -----------------LIBRARIES IMPORTS------------------- //
import {
	Button,
	Input,
	ModalConfirmation,
	Typography,
} from 'design_system/src';

// -----------------DATA IMPORTS------------------- //

import { ReactComponent as File } from 'design_system/src/static/icons/add-file.svg';
import { ReactComponent as FileTick } from 'design_system/src/static/icons/file-tick.svg';
import { ReactComponent as FileFaq } from 'design_system/src/static/icons/faq-file.svg';
import { ReactComponent as FileUp } from 'design_system/src/static/icons/file-up.svg';
import { ReactComponent as LinkIcon } from 'design_system/src/static/icons/link.svg';
import { ReactComponent as User } from 'design_system/src/static/icons/user-card-2.svg';

// -----------------STATIC IMPORTS------------------- //
import '../../../ozoner.scss';
import {
	createDocuments,
	updateTaxDataFinancialForm,
} from 'models/financial-form/controllers/financial-form.controller';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import {
	ZapsignDocumentStatus,
	ZapsignDocumentStatusValues,
	ZapsignDocumentType,
} from 'models/zapsign/enums/zapsign.enum';
import { ozonerContext } from '../../OzonerContext';

function ZapsignSection({
	canCreateDocs,
	contractId: contractIdProp,
	onUpdateDocs,
}: {
	canCreateDocs: boolean;
	contractId?: string;
	onUpdateDocs?: () => void;
}) {
	const {
		ozoner,
		updateFormData,
		financialForm,
		zapsignDocumentsLoading,
		zapsignDocuments,
		updateZapsignDocuments,
	} = useContext(ozonerContext);

	const [openCreateDocuments, setOpenCreateDocuments] = useState(false);
	const [openRegenerateWarning, setOpenRegenerateWarning] = useState(false);
	const [rfc, setRfc] = useState('');
	const [loadingFiles, setLoadingFiles] = useState(false);

	const contractId =
		contractIdProp ?? financialForm.form.loanApplication?.zapsignContractId;

	useEffect(() => {
		setRfc(
			(financialForm?.form as any)?.taxData?.rfc ||
				ozoner?.curp.substring(0, 10) ||
				'',
		);
	}, [ozoner, financialForm]);

	const downloadFiles = () => {
		if (onUpdateDocs) {
			setLoadingFiles(true);
			onUpdateDocs();
			setLoadingFiles(false);
			return;
		}
		if (!financialForm?.form) return;
		setLoadingFiles(true);
		updateTaxDataFinancialForm(financialForm.form._id, { rfc })
			.then((data) => {
				updateFormData({
					...(financialForm.form as any),
					taxData: (data as any).taxData,
				});
				return createDocuments(financialForm.form._id);
			})
			.then((newForm) => {
				updateFormData(newForm);
				updateZapsignDocuments(false);

				setLoadingFiles(false);
				setOpenCreateDocuments(false);
				toasterNotification({
					msg: 'Documentos generados',
					toasterType: toasterOptions.success,
					style: 'light',
				});
			})
			.catch((e) => {
				setLoadingFiles(false);
				commonErrorHandler(e);
			});
	};
	const copyLinkSignDocuments = () => {
		if (!contractId || !zapsignDocuments[contractId]) return;
		const link = zapsignDocuments[contractId].signers?.[0]?.sign_url;
		navigator.clipboard
			.writeText(link)
			.then(() => {
				toasterNotification({
					msg: 'Link copiado correctamente',
					style: 'light',
					toasterType: toasterOptions.success,
				});
			})
			.catch((e) => {
				commonErrorHandler(e);
			});
	};
	const copyLinkSignedDocuments = () => {
		if (!contractId || !zapsignDocuments[contractId]) return;
		const link = zapsignDocuments[contractId].signed_file;
		if (!link) return;
		navigator.clipboard
			.writeText(link)
			.then(() => {
				toasterNotification({
					msg: 'Link copiado correctamente',
					style: 'light',
					toasterType: toasterOptions.success,
				});
			})
			.catch((e) => {
				commonErrorHandler(e);
			});
	};

	return (
		<>
			{contractId ? (
				<>
					<Typography
						scale="large"
						weight="600"
						className="m_b_xs w_100_per text_center"
					>
						Contrato
					</Typography>
					<div className="display_flex flex_justify_between">
						<Typography scale="medium" weight="600" className="m_b_xs">
							Estado
						</Typography>
						<Typography
							scale="medium"
							weight="400"
							className="m_b_xs"
							textColor={
								(ZapsignDocumentStatusValues[
									zapsignDocuments[contractId]?.status ?? ''
								]?.color ?? 'primary_300') as any
							}
						>
							{ZapsignDocumentStatusValues[
								zapsignDocuments[contractId]?.status ?? ''
							]?.label ?? 'No info'}
						</Typography>
					</div>
					{zapsignDocuments[contractId]?.status ===
					ZapsignDocumentStatus.PENDING ? (
						<Button
							scale="small"
							variant="ghost"
							color="primary"
							onClick={copyLinkSignDocuments}
							icon={<LinkIcon />}
							className="w_100_per m_b_xs"
							disabled={zapsignDocumentsLoading}
						>
							Link firma
						</Button>
					) : (
						<Button
							scale="small"
							variant="ghost"
							onClick={copyLinkSignedDocuments}
							icon={<FileTick />}
							className="w_100_per m_b_xs"
							disabled={zapsignDocumentsLoading}
						>
							Ver Documento
						</Button>
					)}
					<Button
						scale="small"
						onClick={() => {
							updateZapsignDocuments(true);
						}}
						icon={<FileFaq />}
						className="w_100_per m_b_xs"
						disabled={zapsignDocumentsLoading}
					>
						Verificar estado
					</Button>
					<Button
						scale="small"
						variant="link"
						onClick={() => setOpenRegenerateWarning(true)}
						icon={<FileUp />}
						className="w_100_per m_b_xs"
						disabled={loadingFiles || zapsignDocumentsLoading}
					>
						Rehacer contrato
					</Button>
				</>
			) : (
				<Button
					scale="small"
					onClick={() => {
						setOpenCreateDocuments(true);
					}}
					icon={<File />}
					disabled={!canCreateDocs || loadingFiles}
					className="w_100_per"
				>
					Generar documentación
				</Button>
			)}
			{openCreateDocuments && (
				<ModalConfirmation
					openConfirmation={openCreateDocuments}
					handleClose={() => setOpenCreateDocuments(false)}
					icon={<File className="text_primary_300" />}
					widthDesktop={40}
					isConfirmationLoading={false}
					actions={[
						{
							label: 'Cancelar',
							color: 'grey',
							action: () => setOpenCreateDocuments(false),
						},
						{
							label: 'Aceptar',
							color: 'primary',
							action: () => downloadFiles(),
							disabled: !rfc || loadingFiles || !canCreateDocs,
						},
					]}
				>
					<>
						<Typography scale="large" weight="600" className="m_b_xs">
							Generación de documentos{' '}
						</Typography>
						<Typography
							scale="medium"
							weight="400"
							className="m_b_md"
							textColor="neutral_700"
						>
							Para generar el contrato del credito del usuario necesitamos su
							rfc, una vez se creen los documentos se puede proceder a solicitar
							la firma del cliente para proceder con la activacion del credito
						</Typography>
						<Input
							title="RFC del Ozoner"
							icon={<User />}
							value={rfc}
							onChange={(e) => setRfc(e.target.value)}
							name="rfc"
							type="text"
						/>
					</>
				</ModalConfirmation>
			)}
			{openRegenerateWarning && (
				<ModalConfirmation
					openConfirmation={openRegenerateWarning}
					handleClose={() => setOpenRegenerateWarning(false)}
					icon={<FileUp className="text_primary_300" />}
					widthDesktop={40}
					isConfirmationLoading={false}
					actions={[
						{
							label: 'Cancelar',
							color: 'grey',
							action: () => setOpenRegenerateWarning(false),
						},
						{
							label: 'Continuar',
							color: 'primary',
							action: () => {
								setOpenRegenerateWarning(false);
								downloadFiles();
							},
							disabled: loadingFiles,
						},
					]}
					title={
						<Typography scale="large" weight="400">
							¿Deseas{' '}
							<Typography scale="large" weight="600" component="span">
								rehacer el contrato?
							</Typography>
						</Typography>
					}
					subtitle={
						<Typography scale="medium" weight="400" textColor="neutral_700">
							Este contrato se va a recrear, se perderán las firmas realizadas y
							el cliente tendrá que volver a firmarlo en caso de que ya esté
							firmado, ¿desea continuar?
						</Typography>
					}
				/>
			)}
		</>
	);
}
export default ZapsignSection;
